<template>
    <div class="home">
        <b-container fluid>
            <b-row>
              <b-col>
                <h1>Application Transaction Details</h1>
              </b-col>
            </b-row>

            <div class="card">
              <div class="card-header">Applicant Information</div>
              <div class="card-body">                
                  <b-row>
                      <b-col cols="4"><BaseTextOutput label="Last Name" :value="application.lastName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="First Name" :value="application.firstName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Middle Name" :value="application.middleName"/></b-col>
                  </b-row>

                  <Address :value="application.address" :international="application.international"/>

                  <b-row class="mt-3">
                      <b-col cols="4"><BaseTextOutput label="Preferred Phone" :value="application.preferredPhone"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Email Address" :value="application.emailAddress"/></b-col>
                  </b-row>

                  <b-row v-if="!hasRole('finance-staff')">
                      <b-col cols="4"><BaseTextOutput label="Date of Birth" :value="application.license.dateOfBirth"/></b-col>
                  </b-row>
              </div>
            </div>

            <div class="card">
              <div class="card-header">Transaction Detail</div>
              <div class="card-body">                
                  <b-row>
                      <b-col>
                       <b-table-simple hover small caption-top responsive>                          
                          <b-thead head-variant="dark">
                            <b-tr>
                              <b-th>Date</b-th>
                              <b-th>Description</b-th>
                              <b-th>Amount</b-th>
                              <b-th>Status</b-th>
                              <b-th>Payment Method</b-th>
                              <b-th>Confirmation #</b-th>
                            </b-tr>
                          </b-thead>
                          <b-tbody>
                            <b-tr v-for="(tx, index) in transactions" :key="tx.paymentId">
                              <b-td>{{ tx.date }}</b-td>
                              <b-td>{{ tx.description }}</b-td>
                              <b-td>{{ tx.amount | currency }}</b-td>
                              <b-td>{{ tx.status }}</b-td>
                              <b-td>
                                  {{ tx.paymentType && tx.paymentType.description ? tx.paymentType.description : "" }}
                                  {{ tx.checkPaymentType && tx.checkPaymentType.description
                                  ? ' - ' + tx.checkPaymentType.description : "" }}
                              </b-td>
                              <b-td>{{ tx.confirmationNumber }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td colspan="7" variant="secondary" class="text-right">
                                    Total Due: <b>{{totalDue | currency}}</b>
                                </b-td>
                            </b-tr>
                          </b-tbody>
                        </b-table-simple>                        
                      </b-col>
                  </b-row>
              </div>
            </div>

            <div class="card" v-if="totalDue != 0 && hasRole('finance-staff') && isPending()">
              <div class="card-header">Collect Check Payment</div>
              <div class="card-body">   

                <b-row>
                    <b-col cols="4">
                        <BaseSelector v-model="paymentType"
                                      :v="$v.paymentType"
                                      label="Payment Type"
                                      slug="paymentType"
                                      :options=opts.payTypeOptions
                                      setId="paymentType"
                        />
                    </b-col>
                    <b-col cols="4">
                      <BaseTextInput v-model="checkNumber"
                                            :v="$v.checkNumber"
                                            label="Check Number"
                                            slug="checkNumber"
                                            setId="checkNumber"
                      />
                    </b-col>
                    <b-col>
                      <b-button variant="primary" class="mr-3" style="margin-top:32px;" @click="submitCheckPayment">Submit</b-button>
                    </b-col>
                </b-row>
    
              </div>
            </div>

            <div class="card" v-if="hasRole('finance-staff') && (isActive() || isPending())
            && application.paymentStatus === 'CONFIRMED'">
                <div class="card-header">Refund Payment or Mark as NSF</div>
                <div class="card-body">

                    <b-row>
                        <b-col cols="2" v-if="hasConfirmedLateFee()">
                            <BaseCheckboxInput v-model="lateFeeOnly"
                                               :v="$v.lateFeeOnly"
                                               label="Refund Late Fee Only"
                                               slug="lateFeeOnly"
                            />
                        </b-col>
                        <b-col cols="4" class="border-right border-secondary">
                            <b-button variant="primary" class="mr-3" @click="refundPayment">Refund Payment</b-button>
                        </b-col>
                        <b-col cols="2">
                            <BaseCheckboxInput v-model="applyFee"
                                               :v="$v.applyFee"
                                               label="Apply NSF Fee"
                                               slug="applyFee"
                            />
                        </b-col>
                        <b-col cols="4">
                            <b-button variant="secondary" class="mr-3" @click="markNsfPayment">Mark as NSF</b-button>
                        </b-col>
                    </b-row>

                </div>
            </div>

        </b-container>

    </div>
</template>

<script>
import axios from 'axios'
import { required, maxLength } from 'vuelidate/lib/validators'
import Address from '@/components/templates/Address.vue'

export default {
  components: {
    Address
  },

  data: () => ({
    application: {},
    transactions: [],
    checkNumber: null,
    paymentType: null,
    applyFee: true,
    lateFeeOnly: false,
    opts:{
        payTypeOptions: [],
    }
  }),
  
  mounted() {
    this.loadApplication()
    this.loadTransactions()
    this.loadPayTypeOptions()
  },

  validations: {
    checkNumber: { required, maxLength: maxLength(13) },
    paymentType: { required },
    applyFee: { required: false },
    lateFeeOnly: { required: false },
  },

  computed: {
    totalDue: function() {
      var total = 0

      this.transactions.forEach(function(tx) { 
        if (tx.status === 'PENDING') {
          total = total + tx.amount
        }
      })

      return total
    }
  },

  methods: {
    isPending() {
        return this.application.applicationStatus === "PENDING"
    },
    isActive() {
        return this.application.license.status === "ACTIVE"
    },
    hasConfirmedLateFee() {
        return this.transactions.some(t => t.description === "Late Renewal Fee" && t.status === "CONFIRMED")
    },

    loadApplication() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/${this.$route.params.id}`)
        .then(response => { 
          this.application = response.data 
        })
    },

    loadTransactions() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/payment/${this.$route.params.id}`)
        .then(response => { 
          this.transactions = response.data 
        })
    },

    loadPayTypeOptions() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/public/payTypes`, { ignoreHeader: true })
            .then(response => {
                for (let type of response.data) {
                    this.opts.payTypeOptions.push({
                        value: type.code,
                        text: type.description
                    })
                }
            })
    },

    submitCheckPayment() {
      const payload = {
        applicationId: this.$route.params.id,
        checkNumber: this.checkNumber,
        paymentType: this.paymentType
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/fees/payment/check`, payload)
        .then(response => { 
           this.loadTransactions()
           this.showRootToast("Application Updated", "Payment has been applied to application.", "success")
      })
    },

    refundPayment() {
        const payload = {
            applicationId: this.application.applicationId,
            hasFee: this.lateFeeOnly
        }


        axios.post(`${process.env.VUE_APP_API_BASE_URL}/fees/refund`, payload)
            .then(response => {
                this.loadTransactions()
                this.showRootToast("Application Updated", "Refund has been applied to application.", "success")
            })
    },

    markNsfPayment() {
        const payload = {
            applicationId: this.application.applicationId,
            hasFee: this.applyFee
        }

        axios.post(`${process.env.VUE_APP_API_BASE_URL}/fees/nsf`, payload)
            .then(response => {
                this.loadTransactions()
                this.showRootToast("Application Updated", "Application payment has been marked NSF.", "success")
            })
    },

    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
            title: title,
            toaster: 'b-toaster-top-center',
            solid: true,
            variant: type,
            appendToast: true,
            noAutoHide:true
          })
        this.toastFocus()
    },
    toastFocus() {
        setTimeout(() => {
            let elem = document.getElementsByClassName("toast")
            elem = elem[elem.length-1]
            elem.focus()
        }, 100)
    },
    hasRole(roles) {
        if (this.$keycloak) {
            return this.$keycloak.hasResourceRole(roles)
        } else {
            return false;
        }
    },
  },
  created() {
      document.title = "Application Transaction Details - Body Art"
  }
}
</script>

<style>
</style>